import React, { useState } from 'react';
import { Stack, Typography, ButtonBase, Snackbar, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import EmailIcon from '../icons/EmailIcon';
import FacebookIcon from '../icons/FacebookIcon';
import WhatsAppIcon from '../icons/WhatsAppIcon';
import InstagramIcon from '../icons/InstagramIcon';
import GMBIcon from '../icons/GMBIcon';
import CopyLinkIcon from '../icons/CopyLinkIcon';

import logger from '../../../utils/logger';

const SharingContainer = styled(Stack)(({ theme }) => ({
  gap: '12px',
  alignItems: 'flex-start',
  maxWidth: '250px',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
    width: '100%',
    alignItems: 'stretch'
  }
}));

const SharingOption = styled(ButtonBase)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10.67px',
  padding: '8px 12px',
  borderRadius: '8px',
  width: '100%',
  justifyContent: 'flex-start',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(11, 145, 214, 0.04)'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '12px 16px',
    minHeight: '48px',
    gap: '12px',
    border: '1px solid rgba(0, 0, 0, 0.12)',
    backgroundColor: '#fff',
    '&:hover': {
      backgroundColor: 'rgba(11, 145, 214, 0.08)',
      borderColor: '#0B91D6'
    }
  }
}));

const OptionText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 300,
  color: '#121927',
  textAlign: 'left',
  [theme.breakpoints.down('sm')]: {
    fontSize: '15px',
    fontWeight: 400
  }
}));

const iconComponents = {
  gmb: GMBIcon,
  email: EmailIcon,
  facebook: FacebookIcon,
  whatsapp: WhatsAppIcon,
  instagram: InstagramIcon,
  copy_link: CopyLinkIcon
};

const SharingOptions = ({ sharingOptions, onSocialShare, onOpenScheduleModal }) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const handleOptionClick = async (platform, label) => {
    logger.log(`Sharing via ${platform}`);

    const postUrl = window.location.href;
    const postTitle = 'Check out my Visibeen post!';
    const postDescription = 'I created this post with Visibeen. Take a look!';

    if (platform === 'gmb') {
      // GMB sharing - open schedule post modal
      if (onOpenScheduleModal) {
        onOpenScheduleModal();
      } else {
        setSnackbarMessage('Schedule post feature not available');
        setSnackbarOpen(true);
      }
    } else if (platform === 'copy_link') {
      try {
        await navigator.clipboard.writeText(postUrl);
        setSnackbarMessage('Link copied to clipboard!');
        setSnackbarOpen(true);
      } catch (err) {
        logger.error('Failed to copy link:', err);
        setSnackbarMessage('Failed to copy link');
        setSnackbarOpen(true);
      }
    } else if (platform === 'email') {
      // Email sharing
      const mailtoLink = `mailto:?subject=${encodeURIComponent(postTitle)}&body=${encodeURIComponent(postDescription + '\n\n' + postUrl)}`;
      window.open(mailtoLink, '_blank');
      setSnackbarMessage(`Email client opened!`);
      setSnackbarOpen(true);
    } else if (platform === 'facebook') {
      // Facebook sharing
      const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(postUrl)}`;
      window.open(facebookUrl, '_blank', 'width=600,height=400');
      setSnackbarMessage(`Shared via Facebook!`);
      setSnackbarOpen(true);
    } else if (platform === 'whatsapp') {
      // WhatsApp sharing - share the generated template image
      const postElement = document.querySelector('.post-preview-container');
      if (!postElement) {
        setSnackbarMessage('Could not find post to share');
        setSnackbarOpen(true);
        return;
      }

      try {
        const html2canvas = (await import('html2canvas')).default;
        const canvas = await html2canvas(postElement, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          logging: false
        });

        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png', 1.0));
        const file = new File([blob], `visibeen-post-${Date.now()}.png`, { type: 'image/png' });

        // Check if Web Share API with files is supported
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: 'Visibeen Testimonial Post',
            text: 'Check out this testimonial!'
          });
          setSnackbarMessage('Shared via WhatsApp!');
        } else {
          // Fallback: Download the image and show instruction
          const link = document.createElement('a');
          link.download = `visibeen-post-${Date.now()}.png`;
          link.href = canvas.toDataURL('image/png', 1.0);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setSnackbarMessage('Image downloaded! Open WhatsApp and share the downloaded image.');
        }
        setSnackbarOpen(true);
      } catch (err) {
        logger.error('Failed to share via WhatsApp:', err);
        setSnackbarMessage('Failed to share. Please download the image and share manually.');
        setSnackbarOpen(true);
      }
    } else if (platform === 'instagram') {
      // Instagram doesn't have a direct sharing API, so we'll show a message
      setSnackbarMessage(`To share on Instagram, please download the image first and upload it to your Instagram account.`);
      setSnackbarOpen(true);
    } else {
      // Handle other social sharing platforms
      if (onSocialShare) {
        onSocialShare(platform);
      }
      setSnackbarMessage(`Shared via ${label}!`);
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <>
      <SharingContainer>
        {sharingOptions.map((option) => {
          const IconComponent = iconComponents[option.platform];
          return (
            <SharingOption
              key={option.platform}
              onClick={() => handleOptionClick(option.platform, option.label)}
            >
              {IconComponent && (
                <IconComponent width={20} height={20} color="currentColor" />
              )}
              <OptionText>{option.label}</OptionText>
            </SharingOption>
          );
        })}
      </SharingContainer>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SharingOptions;